import { ScannerError, normalizeError } from '../utils/errors.js';

async function applyConstraint(track, constraint) {
    if (!track?.applyConstraints) {
        throw new ScannerError(
            'CAMERA_CONSTRAINTS_UNAVAILABLE',
            'O navegador não permite alterar recursos da câmera em tempo real.',
            { context: 'camera.constraints' }
        );
    }

    try {
        await track.applyConstraints({ advanced: [constraint] });
    } catch (advancedError) {
        try {
            await track.applyConstraints(constraint);
        } catch {
            throw normalizeError(advancedError, 'camera.constraints');
        }
    }
}

export async function enableContinuousAutofocus({
    track,
    cameraInfo,
    logger,
}) {
    if (!cameraInfo?.capabilities.continuousAutofocus) {
        logger?.warn(
            'camera.autofocus.unavailable',
            'Autofocus contínuo indisponível nesta câmera.'
        );

        return { supported: false, applied: false };
    }

    try {
        await applyConstraint(track, { focusMode: 'continuous' });
        logger?.info(
            'camera.autofocus.enabled',
            'Autofocus contínuo habilitado.'
        );

        return { supported: true, applied: true };
    } catch (error) {
        const normalized = normalizeError(error, 'camera.autofocus');

        logger?.error(
            'camera.autofocus.failed',
            'Autofocus suportado, mas não pôde ser habilitado.',
            normalized
        );

        return {
            supported: true,
            applied: false,
            error: normalized.message,
        };
    }
}

export async function applyTorch({
    track,
    cameraInfo,
    enabled,
    logger,
}) {
    if (!cameraInfo?.capabilities.torch) {
        throw new ScannerError(
            'TORCH_UNAVAILABLE',
            'Torch indisponível nesta câmera.',
            { context: 'camera.torch' }
        );
    }

    await applyConstraint(track, { torch: Boolean(enabled) });
    logger?.info(
        'camera.torch.changed',
        `Lanterna ${enabled ? 'ativada' : 'desativada'}.`,
        { enabled: Boolean(enabled) }
    );
}

export async function applyZoom({
    track,
    cameraInfo,
    value,
    logger,
}) {
    const zoom = cameraInfo?.capabilities.zoom;

    if (!zoom?.supported) {
        throw new ScannerError(
            'ZOOM_UNAVAILABLE',
            'Zoom indisponível nesta câmera.',
            { context: 'camera.zoom' }
        );
    }

    const parsed = Number(value);
    const clamped = Math.min(zoom.max, Math.max(zoom.min, parsed));

    await applyConstraint(track, { zoom: clamped });
    logger?.info(
        'camera.zoom.changed',
        `Zoom alterado para ${clamped}.`,
        { value: clamped }
    );

    return clamped;
}
